import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, setSessionToken, clearSessionToken, getSessionToken } from "./api";
import { initPushNotifications, unregisterPushNotifications } from "./nativePush";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Whenever the user becomes authenticated, fire FCM registration. Internal
  // guards prevent duplicate listeners across re-renders / re-mounts.
  useEffect(() => {
    if (!user) return undefined;
    initPushNotifications({
      onDeepLink: (path) => {
        try { window.location.hash = `#${path}`; } catch {/* ignore */}
      },
    });
    return undefined;
  }, [user?.id]);

  useEffect(() => {
    // If neither cookie nor sessionStorage token is present, skip the /me probe.
    if (!getSessionToken() && !document.cookie.includes("hp_access")) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    api
      .get("/auth/me")
      .then((r) => {
        if (cancelled) return;
        setUser(r.data);
        setIsImpersonating(!!r.data?.impersonated_by);
      })
      .catch(() => clearSessionToken())
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (emailOrPayload, password) => {
    const payload =
      typeof emailOrPayload === "object" ? emailOrPayload : { email: emailOrPayload, password };
    const { data } = await api.post("/auth/login", payload);
    // Store the Bearer token as a cross-origin fallback (cookies are primary).
    if (data.access_token) setSessionToken(data.access_token);
    setUser(data.user);
    setIsImpersonating(!!data.user?.impersonated_by);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const logout = async () => {
    // Fire push unregister non-blocking — Capacitor bridge can stall on
    // Android 16; we don't let that freeze the logout flow.
    try { unregisterPushNotifications(); } catch {/* ignore */}
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout API call failed", e?.message);
    }
    clearSessionToken();
    setUser(null);
    setIsImpersonating(false);
    window.location.href = "/login";
  };

  const updateShop = async (payload) => {
    const { data } = await api.put("/auth/shop", payload);
    setUser((u) => ({ ...u, ...data }));
    return data;
  };

  // ---- Impersonation ----
  const startImpersonation = async (shopId) => {
    const { data } = await api.post(`/admin/shops/${shopId}/impersonate`);
    if (data.access_token) setSessionToken(data.access_token);
    setUser(data.user);
    setIsImpersonating(true);
    window.location.href = "/";
  };

  const exitImpersonation = async () => {
    try {
      const { data } = await api.post("/admin/exit-impersonation");
      // Server already swapped cookies; clear session token so next /me uses the restored cookie.
      // If the cookie path failed (cross-origin without ACAO echo), the server response also
      // returns the admin's token so we can keep working via Authorization header.
      if (data?.access_token) setSessionToken(data.access_token);
      else clearSessionToken();
    } catch (e) {
      console.warn("Exit impersonation failed", e?.message);
      await logout();
      return;
    }
    setIsImpersonating(false);
    window.location.href = "/admin";
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateShop,
        formatApiError,
        startImpersonation,
        exitImpersonation,
        isImpersonating,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
